import Image from "next/image";

import { ButtonLink } from "@/components/buttonLink/buttonLink.component";
import { RichText } from "@/components/richText/richText.component";

import { resolveCtaLink } from "@/utils/url.util";

import type { GetPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export function OptionCardGridItem({ card }: { card: OptionCardGridItemData }) {
  const imageUrl = card.image?.asset
    ? urlFor(card.image).width(640).height(360).url()
    : null;
  const mobileImageUrl = card.mobileImage?.asset
    ? urlFor(card.mobileImage).width(800).height(450).url()
    : null;
  const cta = resolveCtaLink(card);

  return (
    <div
      data-testid="option-card-grid-item"
      className="border-greyscale-strength/30 flex h-full flex-col overflow-hidden rounded-2xl border bg-white">
      {imageUrl ? (
        <div className="relative aspect-16/10 w-full overflow-hidden">
          {mobileImageUrl ? (
            <Image
              src={mobileImageUrl}
              alt={card.mobileImage?.alt ?? card.image?.alt ?? ""}
              fill
              className="object-cover md:hidden"
              sizes="100vw"
            />
          ) : null}
          <Image
            src={imageUrl}
            alt={card.image?.alt ?? ""}
            fill
            className={
              mobileImageUrl ? "hidden object-cover md:block" : "object-cover"
            }
            sizes="(max-width: 767px) 100vw, 33vw"
          />
        </div>
      ) : null}

      <div className="flex flex-col items-center px-6 py-8 text-center">
        {card.badge && (
          <div className="text-success mb-3 w-full text-center tracking-wide">
            <RichText>{card.badge}</RichText>
          </div>
        )}

        {card.title && (
          <div className="text-greyscale-headline mb-4 w-full text-center">
            <RichText>{card.title}</RichText>
          </div>
        )}

        {card.description ? (
          <div className="text-greyscale-body mb-8 w-full flex-1 text-center leading-relaxed">
            <RichText>{card.description}</RichText>
          </div>
        ) : (
          <div className="mb-8 flex-1" />
        )}

        {cta && (
          <div className="mt-auto flex w-full flex-col items-center gap-4">
            <ButtonLink {...cta} />
          </div>
        )}

        {card.legal && (
          <div className="text-greyscale-body mt-5 w-full max-w-xs text-center text-sm leading-relaxed">
            <RichText>{card.legal}</RichText>
          </div>
        )}
      </div>
    </div>
  );
}

type PageBuilderBlock = NonNullable<
  NonNullable<GetPageQueryResult>["pageBuilder"]
>[number];

export type OptionCardGridBlock = Extract<
  PageBuilderBlock,
  { _type: "optionCardGrid" }
>;

export type OptionCardGridItemData = NonNullable<
  NonNullable<OptionCardGridBlock["cards"]>[number]
>;
